(function initSuoDiagnosisLogger(root) {
  "use strict";

  const ALLOWED_EVENTS = new Set([
    "diagnosis_start",
    "answer_selected",
    "diagnosis_complete",
    "product_click"
  ]);

  const QUEUE_CAP = 50;
  const STORAGE_KEY = "suo_diag_log_queue";

  function createId() {
    if (root.crypto && typeof root.crypto.randomUUID === "function") {
      return root.crypto.randomUUID();
    }
    return `diag-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
  }

  function getDeviceType() {
    const width = root.screen && root.screen.width ? root.screen.width : root.innerWidth;
    if (width <= 767) return "mobile";
    if (width <= 1024) return "tablet";
    return "desktop";
  }

  function getAttribution() {
    const params = new URLSearchParams(root.location ? root.location.search : "");
    return {
      referrer: root.document ? root.document.referrer : "",
      utm_source: params.get("utm_source") || "",
      utm_medium: params.get("utm_medium") || "",
      utm_campaign: params.get("utm_campaign") || "",
      device_type: getDeviceType(),
      screen_width: root.screen && root.screen.width ? root.screen.width : root.innerWidth || "",
      page_url: root.location ? root.location.href : ""
    };
  }

  // Which storefront this diagnosis runs on, so a shared log can split 自社(Shopify)
  // and 楽天 results. Prefer the explicit config flag; otherwise infer from the host.
  function getPlatform() {
    const config = root.SUO_DIAG_CONFIG || {};
    if (config.platform) return config.platform;
    const host = (root.location && root.location.hostname) || "";
    if (/rakuten\.co\.jp/i.test(host)) return "rakuten";
    if (/suo\.co\.jp/i.test(host)) return "shopify";
    return "unknown";
  }

  let diagnosisId = "";
  const attribution = getAttribution();

  function startSession() {
    diagnosisId = createId();
    return diagnosisId;
  }

  function getSessionId() {
    return diagnosisId;
  }

  function getSessionStorage() {
    try {
      if (root.sessionStorage && typeof root.sessionStorage.getItem === "function") {
        return root.sessionStorage;
      }
    } catch (_error) {
      // Accessing sessionStorage can throw (e.g. disabled cookies); treat as absent.
    }
    return null;
  }

  function loadQueue() {
    const store = getSessionStorage();
    if (!store) return [];
    try {
      const raw = store.getItem(STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (_error) {
      return [];
    }
  }

  // In-memory queue is the source of truth; sessionStorage is a best-effort mirror.
  let queue = loadQueue();

  function persistQueue() {
    const store = getSessionStorage();
    if (!store) return;
    try {
      store.setItem(STORAGE_KEY, JSON.stringify(queue));
    } catch (_error) {
      // Storage may be full or unavailable; the in-memory queue still works.
    }
  }

  function enqueue(item) {
    queue.push(item);
    // Drop-oldest to bound memory and storage usage.
    while (queue.length > QUEUE_CAP) {
      queue.shift();
    }
    persistQueue();
  }

  function removeFromQueue(item) {
    const index = queue.indexOf(item);
    if (index !== -1) {
      queue.splice(index, 1);
      persistQueue();
    }
  }

  // Read transport functions at call time so tests can stub globalThis.fetch.
  function deliver(item) {
    const config = root.SUO_DIAG_CONFIG || {};
    const endpoint = config.logEndpoint;
    if (!endpoint) return false;

    const body = JSON.stringify(item.payload);

    try {
      if (item.payload.event_name === "product_click" &&
          root.navigator && typeof root.navigator.sendBeacon === "function") {
        const ok = root.navigator.sendBeacon(
          endpoint,
          new Blob([body], { type: "text/plain;charset=UTF-8" })
        );
        if (ok) {
          removeFromQueue(item);
        }
        return ok;
      }

      const fetchFn = root.fetch;
      if (typeof fetchFn !== "function") return false;

      const result = fetchFn(endpoint, {
        method: "POST",
        mode: "no-cors",
        keepalive: true,
        headers: { "Content-Type": "text/plain;charset=UTF-8" },
        body
      });

      // On success remove from queue; on rejection keep it for a later retry.
      if (result && typeof result.then === "function") {
        result.then(function () {
          removeFromQueue(item);
        }, function () {
          // Keep queued; do not remove.
        });
      } else {
        removeFromQueue(item);
      }
      return true;
    } catch (_error) {
      // Keep item queued for retry.
      return false;
    }
  }

  function flushQueue() {
    const config = root.SUO_DIAG_CONFIG || {};
    if (!config.logEndpoint) return;
    // Snapshot to avoid mutating while iterating (deliver may remove items).
    const pending = queue.slice();
    for (let i = 0; i < pending.length; i++) {
      deliver(pending[i]);
    }
  }

  function send(eventName, details) {
    const config = root.SUO_DIAG_CONFIG || {};
    if (!config.logEndpoint || !ALLOWED_EVENTS.has(eventName)) return false;
    if (!diagnosisId) startSession();

    const payload = {
      event_timestamp: new Date().toISOString(),
      diagnosis_id: diagnosisId,
      event_name: eventName,
      platform: getPlatform(),
      ...attribution,
      ...(details || {})
    };

    const item = { payload };
    enqueue(item);

    // Retry any previously failed deliveries first, then this one.
    flushQueue();

    return true;
  }

  // Attach lifecycle flush listeners only where supported.
  if (root.document && typeof root.document.addEventListener === "function") {
    root.document.addEventListener("visibilitychange", function () {
      if (root.document.visibilityState === "hidden") {
        flushQueue();
      }
    });
  }
  if (typeof root.addEventListener === "function") {
    root.addEventListener("pagehide", function () {
      flushQueue();
    });
  }

  const api = { ALLOWED_EVENTS, createId, getDeviceType, startSession, getSessionId, send };

  // Internal, non-public test hooks (underscore-prefixed, not part of documented API).
  api._getQueue = function () { return queue; };
  api._queueLength = function () { return queue.length; };
  api._flush = flushQueue;
  api._resetQueue = function () {
    queue = [];
    persistQueue();
  };

  root.SUO_DIAG_LOGGER = api;
  if (typeof module !== "undefined" && module.exports) {
    module.exports = api;
  }
})(typeof window !== "undefined" ? window : globalThis);
